export type MfaGateMode = "enroll" | "challenge";

export type MfaGateResult = { mode: "challenge"; code: string } | { mode: "enroll" };

type GateRequest = {
  mode: MfaGateMode;
  reason: string;
  resolve: (result: MfaGateResult) => void;
  reject: (error: Error) => void;
};

type Listener = () => void;

let current: GateRequest | null = null;
const listeners = new Set<Listener>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

export function getMfaGateRequest(): GateRequest | null {
  return current;
}

export function subscribeMfaGate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Opens the shared MFA gate UI (enroll wizard or TOTP challenge).
 * Rejects if the user closes/cancels the modal.
 */
export function promptMfaGate(options: { mode: MfaGateMode; reason: string }): Promise<MfaGateResult> {
  if (current) {
    current.reject(new Error("Another two-factor challenge is already open"));
    current = null;
  }

  return new Promise((resolve, reject) => {
    current = {
      mode: options.mode,
      reason: options.reason,
      resolve: (result) => {
        current = null;
        notify();
        resolve(result);
      },
      reject: (error) => {
        current = null;
        notify();
        reject(error);
      },
    };
    notify();
  });
}

export function completeMfaGate(result: MfaGateResult) {
  current?.resolve(result);
}

export function cancelMfaGate(error: Error) {
  current?.reject(error);
}

/** @deprecated Prefer promptMfaGate({ mode: "challenge", reason }) */
export function promptMfaSessionChallenge(reason: string): Promise<string> {
  return promptMfaGate({ mode: "challenge", reason }).then((result) => {
    if (result.mode !== "challenge") {
      throw new Error("Expected MFA challenge result");
    }
    return result.code;
  });
}

/** @deprecated Prefer getMfaGateRequest */
export function getMfaSessionChallengeRequest() {
  const request = getMfaGateRequest();
  if (!request || request.mode !== "challenge") return null;
  return {
    reason: request.reason,
    resolve: (code: string) => completeMfaGate({ mode: "challenge", code }),
    reject: request.reject,
  };
}

/** @deprecated Prefer subscribeMfaGate */
export function subscribeMfaSessionChallenge(listener: Listener): () => void {
  return subscribeMfaGate(listener);
}

/** @deprecated Prefer completeMfaGate */
export function completeMfaSessionChallenge(code: string) {
  completeMfaGate({ mode: "challenge", code });
}

/** @deprecated Prefer cancelMfaGate */
export function cancelMfaSessionChallenge(error: Error) {
  cancelMfaGate(error);
}
